import { type ReactNode, useId } from 'react';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import {
  type FieldValues,
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

export interface FormPageProps<TFieldValues extends FieldValues> {
  title: string;
  form: UseFormReturn<TFieldValues>;
  onSubmit: SubmitHandler<TFieldValues>;
  /** Volta para a lista (Cancelar / seta de voltar). */
  onCancel: () => void;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /** Largura máxima da coluna de conteúdo. Default 900px. */
  contentMaxWidth?: number;
  /** Erro a exibir acima dos campos (ex.: erro 500 do servidor). */
  serverError?: string | null;
}

/**
 * Wrapper de formulário de CRUD renderizado **dentro da área de conteúdo**
 * (à direita do menu, abaixo da barra) — usado nos cadastros principais
 * (empresa/colaborador/cliente). Substitui a lista enquanto o form está aberto;
 * Salvar/Cancelar chamam `onSubmit`/`onCancel`, que devolvem para a lista.
 *
 * Diferente do `FormDialog`, não é um modal (não cobre o menu/topo). As ações
 * ficam numa barra fixa ao pé da área de conteúdo para formulários longos.
 */
export function FormPage<TFieldValues extends FieldValues>({
  title,
  form,
  onSubmit,
  onCancel,
  children,
  submitLabel,
  cancelLabel,
  contentMaxWidth = 900,
  serverError,
}: FormPageProps<TFieldValues>): JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const isSubmitting = form.formState.isSubmitting;
  const colSx = { maxWidth: contentMaxWidth, mx: 'auto', width: '100%' } as const;

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        aria-labelledby={titleId}
        // stopPropagation: sub-recursos (endereços, docs fiscais, taxas…) abrem
        // FormDialogs aninhados DENTRO deste form. O submit deles borbulha pela
        // árvore React até este form ancestral; sem isso, salvar o form interno
        // dispararia também este submit.
        onSubmit={(e) => {
          e.stopPropagation();
          void form.handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, ...colSx }}>
          <Tooltip title={cancelLabel ?? t('components.formDialog.cancel')}>
            <IconButton
              aria-label={cancelLabel ?? t('components.formDialog.cancel')}
              onClick={onCancel}
              disabled={isSubmitting}
              edge="start"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography id={titleId} variant="h5" component="h1">
            {title}
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, ...colSx }}>
          <Stack spacing={2}>
            {serverError && (
              <Box
                role="alert"
                sx={{
                  bgcolor: 'error.lighter',
                  color: 'error.dark',
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'error.light',
                  fontSize: 14,
                }}
              >
                {serverError}
              </Box>
            )}
            {children}
          </Stack>
        </Paper>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            mt: 2,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            ...colSx,
          }}
        >
          <Button onClick={onCancel} disabled={isSubmitting}>
            {cancelLabel ?? t('components.formDialog.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting
              ? t('components.formDialog.saving')
              : (submitLabel ?? t('components.formDialog.save'))}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
